import type React from 'react';
import ProgramSubCurrentSystemNew from '../components/form-sections/programs/program-sub-current-system-new.tsx';
import ProgramSubPainPointsNew from '../components/form-sections/programs/program-sub-pain-points-new.tsx';
import ProgramSubStructure from '../components/form-sections/programs/program-sub-program-structure.tsx';
import ProgramSubScoping from '../components/form-sections/programs/program-sub-scoping.tsx';
import ProjectsSubCurrentSystem from '../components/form-sections/projects/projects-sub-current-system-new.tsx';
import ProjectsSubPainPoints from '../components/form-sections/projects/projects-sub-pain-points.tsx';
import ProjectsSubScoping from '../components/form-sections/projects/projects-sub-scoping.tsx';
import ProjectsSubStructure from '../components/form-sections/projects/projects-sub-structure.tsx';
import PropertiesSubCurrentSystem from '../components/form-sections/properties/properties-sub-current-system.tsx';
import PropertiesSubLegacyData from '../components/form-sections/properties/properties-sub-legacy-data.tsx';
import PropertiesSubScoping from '../components/form-sections/properties/properties-sub-scoping.tsx';
import PropertiesSubTrackingToday from '../components/form-sections/properties/properties-sub-tracking-today.tsx';
import { SectionCurrentSystems } from '../components/form-sections/section-current-systems.tsx';
import { SectionEntryContactInfo } from '../components/form-sections/section-entry-contact-info.tsx';
import { SectionRoleAndContext } from '../components/form-sections/section-role-and-context.tsx';
import NewReviewSubmit from '../components/new-review-submit.tsx';
import type { ValidationChange } from '../types/base-entity';
import type { EntryContactInfo, IntakePayload, PrimaryObjectOfWorkItem } from '../types/intake.types';

export type ObjectType = 'programs' | 'projects' | 'properties';

export type StepItem = {
  key: string;
  title: string;
  Component: React.ComponentType<ValidationChange>;
};

const CONTEXT_STEP_COUNT = 4;

export function isEntryContactComplete(entryContact: EntryContactInfo | null | undefined): boolean {
  if (!entryContact) return false;

  const { fullName, email, phone, preferredContact, cityOrAgencyName, department } = entryContact;

  const contactMethodFilled =
    preferredContact?.toString().trim() === 'Phone' ? phone?.toString().trim().length > 0 : email?.toString().trim().length > 0;

  return [fullName, cityOrAgencyName, department].every((value) => value?.toString().trim()) && contactMethodFilled;
}

export function hasDiscoveryFocus(primaryObjectOfWork: PrimaryObjectOfWorkItem[] | null | undefined): boolean {
  return (primaryObjectOfWork ?? []).length > 0;
}

export function getBreadcrumbGroup(
  currentStep: number,
  steps: StepItem[],
  activeKey: string
): 'Context' | 'Review' | 'Programs' | 'Projects' | 'Properties' {
  if (currentStep < CONTEXT_STEP_COUNT) return 'Context';
  if (currentStep >= steps.length - 1) return 'Review';
  if (activeKey.startsWith('programs-')) return 'Programs';
  if (activeKey.startsWith('projects-')) return 'Projects';
  if (activeKey.startsWith('properties-')) return 'Properties';
  return 'Review';
}

export function buildSteps(data: IntakePayload, PrimaryObjectOfWork: React.ComponentType<ValidationChange>): StepItem[] {
  const base: StepItem[] = [
    { key: 'contact', title: 'Contact Information', Component: SectionEntryContactInfo },
    { key: 'role', title: 'Role & Context', Component: SectionRoleAndContext },
    { key: 'systems', title: 'Current Systems', Component: SectionCurrentSystems },
    { key: 'object', title: 'Primary Object of Work', Component: PrimaryObjectOfWork },
  ];

  if (data.primaryObjectOfWork.some((o) => 'programs' in o)) {
    base.push(
      { key: 'programs-scoping', title: 'Scoping', Component: ProgramSubScoping },
      { key: 'programs-currentSystems', title: 'Current Systems', Component: ProgramSubCurrentSystemNew },
      { key: 'programs-structure', title: 'Program Structure', Component: ProgramSubStructure },
      { key: 'programs-painPoints', title: 'Needs & Pain Points', Component: ProgramSubPainPointsNew }
    );
  }

  if (data.primaryObjectOfWork.some((o) => 'projects' in o)) {
    base.push(
      { key: 'projects-scoping', title: 'Scoping', Component: ProjectsSubScoping },
      { key: 'projects-currentSystems', title: 'Current Systems', Component: ProjectsSubCurrentSystem },
      { key: 'projects-structure', title: 'Structure', Component: ProjectsSubStructure },
      { key: 'projects-painPoints', title: 'Needs & Pain Points', Component: ProjectsSubPainPoints }
    );
  }

  if (data.primaryObjectOfWork.some((o) => 'properties' in o)) {
    base.push(
      {
        key: 'properties-needs',
        title: 'Scoping',
        Component: PropertiesSubScoping,
      },
      { key: 'properties-scoping', title: 'Property Tracking Today', Component: PropertiesSubTrackingToday },
      {
        key: 'properties-currentSystems',
        title: 'Operational systems (Properties)',
        Component: PropertiesSubCurrentSystem,
      },
      {
        key: 'properties-legacyConsolidation',
        title: 'Legacy Data and Consolidation',
        Component: PropertiesSubLegacyData,
      }
    );
  }

  base.push({ key: 'review', title: 'Review & Submit', Component: NewReviewSubmit });

  return base;
}
